import React, { useCallback, useEffect, useState } from 'react';
import { Button } from "antd";
import { AuthPageContainer } from '../Register/AuthPageContainer';
import { AuthLogo } from './ClinicLogo';
import { Channel, otpErrorText } from './constants';
import { useIsMounted } from './useIsMounted';

const lockIcon = (
    <svg width="14" height="14" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <path fill="currentColor" d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zM9 6c0-1.66 1.34-3 3-3s3 1.34 3 3v2H9V6zm3 13c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2z" />
    </svg>
);

const isEmail = (delivery) => delivery?.channel === Channel.Email;

const getSentText = (delivery) => {
    const destination = delivery?.maskedDestination ?? "";

    if (isEmail(delivery)) {
        return `הודעה עם קוד חד-פעמי נשלחה לכתובת הדוא"ל ${destination}`;
    }

    // The design spells out the last digits, CAL4 masks them as ***1234.
    const digits = destination.replace(/\D/g, '');

    return `מסרון עם קוד חד-פעמי נשלח למספר הנייד שמסתיים בספרות ${digits}`;
}

export const OtpStep = ({ clinicLogoUrl, delivery, onSubmit, onResend }) => {
    const [code, setCode] = useState("");
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(false);
    const isMounted = useIsMounted();

    // CAL4 rejects a resend inside resendAfterSeconds, so the link stays disabled until then.
    const [secondsUntilResend, setSecondsUntilResend] = useState(delivery?.resendAfterSeconds ?? 0);

    useEffect(() => {
        setSecondsUntilResend(delivery?.resendAfterSeconds ?? 0);
    }, [delivery]);

    useEffect(() => {
        if (secondsUntilResend <= 0) {
            return;
        }

        const timer = setTimeout(() => setSecondsUntilResend(secondsUntilResend - 1), 1000);

        return () => clearTimeout(timer);
    }, [secondsUntilResend]);

    const run = useCallback(async (action) => {
        setBusy(true);
        setError(null);

        const outcome = await action();
        // A verified code unmounts this screen; a resend keeps it.
        if (!isMounted()) {
            return outcome;
        }

        setBusy(false);
        if (!outcome.ok && !outcome.handled) {
            setError(otpErrorText(outcome.result));
        }

        return outcome;
    }, [isMounted]);

    const submit = async () => {
        if (busy || code === "") {
            return;
        }

        await run(() => onSubmit(code));
    }

    const resend = async () => {
        if (busy || secondsUntilResend > 0) {
            return;
        }

        setCode("");
        const outcome = await run(onResend);
        if (!outcome.ok && !outcome.handled) {
            // A refused resend still starts the cooldown, so a double tap cannot spend a message.
            setSecondsUntilResend(delivery?.resendAfterSeconds ?? 0);
        }
    }

    return (
        <AuthPageContainer
            className={'recordAuth'} logo={<AuthLogo url={clinicLogoUrl} />}
            header={'כניסה עם קוד חד-פעמי לרשומה הרפואית'}>
            <div className={"recordOtpBanner"}>{getSentText(delivery)}</div>
            <div className={"authInputContainer"}>
                <div className={"inputContainer"}>
                    <label className={"inputLabel" + (error ? " recordInputError" : "")} htmlFor="otpCode">
                        <input
                            id="otpCode"
                            autoComplete="one-time-code"
                            inputMode="numeric"
                            placeholder={'להזנת הקוד שקיבלתם'}
                            value={code}
                            onChange={(e) => {
                                setError(null);
                                setCode(e.target.value.replace(/\D/g, ''));
                            }}
                            maxLength={delivery?.codeLength ?? 6} />
                        <span className={"recordLockIcon"}>{lockIcon}</span>
                    </label>
                </div>
                {error && <div className={"recordErrorText"}>{error}</div>}
            </div>
            <Button
                type={"primary"}
                block
                loading={busy}
                disabled={code === ""}
                onClick={submit}>
                אישור
            </Button>
            <div className={"recordResendTitle"}>
                {isEmail(delivery) ? 'לא קיבלתם את ההודעה?' : 'לא קיבלתם את המסרון?'}
            </div>
            <div className={"recordResendText"}>
                לחצו{" "}
                <button
                    type={"button"}
                    className={"recordLinkButton"}
                    disabled={busy || secondsUntilResend > 0}
                    onClick={resend}>
                    כאן
                </button>{" "}
                לשליחה חוזרת{secondsUntilResend > 0 ? ` (עוד ${secondsUntilResend} שניות)` : ""}, במידה ולא קיבלתם את הקוד יש לפנות לגורם ששלח את הרשומה.
            </div>
        </AuthPageContainer>
    );
}
